import React, { useState } from 'react';
import {
  Upload, Loader2, FileText, AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  Users, Briefcase, ArrowRight, RotateCcw, X, Check, Calendar, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ───────────────────────────────────────────────
// Pretty diff cell
// ───────────────────────────────────────────────
const DiffCell = ({ value }) => {
  if (value === null || value === undefined || value === '') return <span className="text-zinc-300 text-xs">—</span>;
  if (typeof value === 'number' && !Number.isInteger(value)) return <span>£{value.toFixed(2)}</span>;
  return <span>{String(value)}</span>;
};

// ───────────────────────────────────────────────
// Collapsible section
// ───────────────────────────────────────────────
const Section = ({ title, count, color = 'gray', defaultOpen = false, children, icon: Icon }) => {
  const [open, setOpen] = useState(defaultOpen);
  const palette = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-zinc-50 border-zinc-200 text-zinc-700',
  }[color];

  return (
    <div className={`border rounded-xl ${palette}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3 text-left"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {Icon && <Icon size={18} />}
        <span className="font-semibold flex-1">{title}</span>
        <span className="px-2.5 py-0.5 bg-white rounded-full text-sm font-bold">{count}</span>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open && <div className="border-t border-current/10 bg-white rounded-b-xl">{children}</div>}
    </div>
  );
};

// ───────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────
const SqueegeeSync = () => {
  const [tab, setTab] = useState('sync'); // sync | schedule-match

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="squeegee-sync-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900">Sync from Squeegee</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Compare your Squeegee data with StreakFree and apply only the changes you approve.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-2 border-b border-zinc-200">
        <button
          onClick={() => setTab('sync')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'sync' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
          }`}
          data-testid="tab-sync"
        >
          <Upload size={14} className="inline mr-2" /> Sync customers & jobs
        </button>
        <button
          onClick={() => setTab('schedule-match')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'schedule-match' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
          }`}
          data-testid="tab-schedule-match"
        >
          <BarChart3 size={14} className="inline mr-2" /> Schedule match report
        </button>
      </div>

      {tab === 'sync' && <SyncTab />}
      {tab === 'schedule-match' && <ScheduleMatchTab />}
    </div>
  );
};

// ───────────────────────────────────────────────
// Sync tab (the original wizard)
// ───────────────────────────────────────────────
const SyncTab = () => {
  const [customersFile, setCustomersFile] = useState(null);
  const [combinedExportDetected, setCombinedExportDetected] = useState(false);
  const [jobsFile, setJobsFile] = useState(null);
  const [step, setStep] = useState('upload'); // upload | preview | applying | done
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [backups, setBackups] = useState([]);

  // Load past backups when on upload step
  React.useEffect(() => {
    if (step !== 'upload') return;
    const loadBackups = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/squeegee-sync/backups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBackups(data.backups || []);
        }
      } catch {}
    };
    loadBackups();
  }, [step]);

  const handleRollbackBackup = async (backupId) => {
    if (!window.confirm('Roll back this apply? Every changed customer/job will be restored to its previous state, and any new customers from that apply will be removed.')) return;
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/squeegee-sync/rollback/${backupId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Rollback failed');
      const data = await res.json();
      toast.success(`Rolled back: ${data.customers_restored} customers, ${data.jobs_restored} jobs, ${data.new_customers_removed} new customers removed`);
      // Reload backups
      setBackups(prev => prev.map(b => b.id === backupId ? { ...b, rolled_back: true } : b));
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Selection state — keyed by id
  const [selectedNewCustomers, setSelectedNewCustomers] = useState(new Set()); // external_ref
  const [selectedNewJobs, setSelectedNewJobs] = useState(new Set()); // indices into preview.new_jobs
  const [selectedRemoveJobs, setSelectedRemoveJobs] = useState(new Set()); // sf_job_id from preview.sf_only_jobs
  const [includeDoneVisits, setIncludeDoneVisits] = useState(false); // recreate past completed visits
  const [selectedCustomerFields, setSelectedCustomerFields] = useState({}); // {sf_customer_id: Set([fields])}
  const [selectedJobFields, setSelectedJobFields] = useState({}); // {sf_job_id: Set([fields])}

  // Result of apply
  const [applyResult, setApplyResult] = useState(null);

  const totalSelected = () => {
    const newC = selectedNewCustomers.size;
    const newJ = selectedNewJobs.size;
    const removeJ = selectedRemoveJobs.size;
    const doneV = includeDoneVisits ? (preview?.done_visits?.length || 0) : 0;
    const custFields = Object.values(selectedCustomerFields).reduce((acc, s) => acc + s.size, 0);
    const jobFields = Object.values(selectedJobFields).reduce((acc, s) => acc + s.size, 0);
    return { newC, newJ, removeJ, doneV, custFields, jobFields, total: newC + newJ + removeJ + doneV + custFields + jobFields };
  };

  // ─── Upload handler ───
  const handleUpload = async () => {
    if (!customersFile || !jobsFile) {
      setError('Please choose both files');
      return;
    }
    setIsUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const fd = new FormData();
      fd.append('customers_csv', customersFile);
      fd.append('jobs_csv', jobsFile);
      const res = await fetch(`${API_URL}/api/squeegee-sync/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Selection helpers ───
  const toggleNewCustomer = (ref) => {
    setSelectedNewCustomers(prev => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
  };
  const toggleAllNewCustomers = () => {
    if (selectedNewCustomers.size === preview.new_customers.length) {
      setSelectedNewCustomers(new Set());
    } else {
      setSelectedNewCustomers(new Set(preview.new_customers.map(c => c.external_ref)));
    }
  };

  const toggleCustomerField = (sfId, field) => {
    setSelectedCustomerFields(prev => {
      const next = { ...prev };
      const set = new Set(next[sfId] || []);
      set.has(field) ? set.delete(field) : set.add(field);
      if (set.size === 0) delete next[sfId];
      else next[sfId] = set;
      return next;
    });
  };
  const selectAllCustomerFields = () => {
    const next = {};
    preview.changed_customers.forEach(c => {
      next[c.sf_customer_id] = new Set(Object.keys(c.diff));
    });
    setSelectedCustomerFields(next);
  };
  const clearCustomerFields = () => setSelectedCustomerFields({});

  const toggleJobField = (jobId, field) => {
    setSelectedJobFields(prev => {
      const next = { ...prev };
      const set = new Set(next[jobId] || []);
      set.has(field) ? set.delete(field) : set.add(field);
      if (set.size === 0) delete next[jobId];
      else next[jobId] = set;
      return next;
    });
  };
  const selectAllJobFields = (fieldName = null) => {
    setSelectedJobFields(prev => {
      const next = { ...prev };
      preview.changed_jobs.forEach(j => {
        const fields = fieldName ? (j.diff[fieldName] ? [fieldName] : []) : Object.keys(j.diff);
        if (!fields.length) return;
        const set = new Set(next[j.sf_job_id] || []);
        fields.forEach(f => set.add(f));
        next[j.sf_job_id] = set;
      });
      return next;
    });
  };
  const clearJobFields = () => setSelectedJobFields({});

  // ─── Preset: align schedule with Squeegee (new customers + next_due only) ───
  const presetAlignSchedule = () => {
    if (!preview) return;
    // Tick all new customers
    setSelectedNewCustomers(new Set(preview.new_customers.map(c => c.external_ref)));
    // Tick next_due + last_done (facts) on every changed job
    const jobNext = {};
    preview.changed_jobs.forEach(j => {
      const fields = [];
      if (j.diff.next_due) fields.push('next_due');
      if (j.diff.last_done) fields.push('last_done');
      if (fields.length) jobNext[j.sf_job_id] = new Set(fields);
    });
    setSelectedJobFields(jobNext);
    // New jobs Squeegee has for existing customers — select all so schedule matches
    setSelectedNewJobs(new Set((preview.new_jobs || []).map((_, i) => i)));
    // Stale SF jobs that no longer exist on Squeegee — remove so the planner matches
    setSelectedRemoveJobs(new Set((preview.sf_only_jobs || []).map(j => j.sf_job_id)));
    // Recreate past completed visits so historic planner days match
    setIncludeDoneVisits((preview.done_visits || []).length > 0);
    // Leave customer-field changes UNTICKED — user only wants schedule alignment, not balance/phone changes
    setSelectedCustomerFields({});
    toast.success(`Selected: ${preview.new_customers.length} new customers + ${(preview.new_jobs || []).length} new jobs + ${Object.keys(jobNext).length} schedule updates + ${(preview.sf_only_jobs || []).length} stale removals + ${(preview.done_visits || []).length} past visits`);
  };

  // ─── Apply ───
  const handleApply = async () => {
    const { total } = totalSelected();
    if (total === 0) {
      toast.error('Nothing selected to apply');
      return;
    }
    if (!window.confirm(`Apply ${total} change${total !== 1 ? 's' : ''}? A backup will be created so this can be rolled back.`)) {
      return;
    }
    setStep('applying');
    try {
      const token = localStorage.getItem('streakfree_token');
      const body = {
        preview_id: preview.preview_id,
        new_customers: [...selectedNewCustomers].map(ref => ({ external_ref: ref })),
        customer_changes: Object.entries(selectedCustomerFields).map(([id, set]) => ({
          sf_customer_id: id, fields: [...set]
        })),
        job_changes: Object.entries(selectedJobFields).map(([id, set]) => ({
          sf_job_id: id, fields: [...set]
        })),
        new_jobs: [...selectedNewJobs],
        remove_job_ids: [...selectedRemoveJobs],
        create_done_visits: includeDoneVisits
      };
      const res = await fetch(`${API_URL}/api/squeegee-sync/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Apply failed');
      }
      const data = await res.json();
      setApplyResult(data);
      setStep('done');
      toast.success('Sync applied successfully');
    } catch (err) {
      toast.error(err.message);
      setStep('preview');
    }
  };

  const handleRollback = async () => {
    if (!window.confirm('Roll back the last apply? This will restore every changed record to its previous state.')) return;
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/squeegee-sync/rollback/${applyResult.backup_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Rollback failed');
      const data = await res.json();
      toast.success(`Rolled back: ${data.customers_restored} customers, ${data.jobs_restored} jobs, ${data.new_customers_removed} new customers removed`);
      setStep('upload');
      setApplyResult(null);
      setPreview(null);
      setSelectedNewCustomers(new Set());
      setSelectedNewJobs(new Set());
      setSelectedRemoveJobs(new Set());
      setIncludeDoneVisits(false);
      setSelectedCustomerFields({});
      setSelectedJobFields({});
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ─── Render ───
  return (
    <>
      {/* ─── STEP: UPLOAD ─── */}
      {step === 'upload' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">1. Customers CSV (Squeegee → Reports → Customers Active)</label>
            <input
              type="file"
              accept=".csv"
              onChange={async (e) => {
                const f = e.target.files[0];
                setCustomersFile(f);
                setCombinedExportDetected(false);
                if (f) {
                  try {
                    const text = await f.text();
                    if (text.slice(0, 2000).includes('Job - Job Ref')) setCombinedExportDetected(true);
                  } catch { /* ignore */ }
                }
              }}
              className="block w-full text-sm border border-zinc-200 rounded-lg p-2"
              data-testid="customers-csv-input"
            />
            {customersFile && <p className="text-xs text-emerald-600 mt-1">✓ {customersFile.name}</p>}
            {combinedExportDetected && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="combined-export-notice">
                <p className="text-sm text-amber-800 font-medium">This looks like a full Squeegee export (customers + jobs in one file).</p>
                <p className="text-xs text-amber-700 mt-1">This compare tool needs two separate CSVs. To import this file directly, use <span className="font-semibold">Sync Centre → Import from Other CRM</span> — it detects this format and imports customers and jobs together automatically.</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">2. Jobs CSV (Squeegee → Reports → All Jobs)</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setJobsFile(e.target.files[0])}
              className="block w-full text-sm border border-zinc-200 rounded-lg p-2"
              data-testid="jobs-csv-input"
            />
            {jobsFile && <p className="text-xs text-emerald-600 mt-1">✓ {jobsFile.name}</p>}
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>What happens:</strong> Files are compared in memory only. Nothing is changed in StreakFree until you tick the changes you want and press Apply.
            </p>
          </div>

          <Button
            onClick={handleUpload}
            disabled={!customersFile || !jobsFile || isUploading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="upload-and-preview-btn"
          >
            {isUploading ? <><Loader2 size={16} className="animate-spin mr-2" /> Comparing…</> : <><Upload size={16} className="mr-2" /> Compare & Preview</>}
          </Button>
        </div>
      )}

      {/* Persistent backup history (only on upload step) */}
      {/* Persistent backup history (only on upload step) */}
      {step === 'upload' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-zinc-900 mb-1">Schedule maintenance</h3>
          <p className="text-xs text-zinc-500 mb-3">
            Find and fix recurring jobs that are showing as "completed" but actually have a future occurrence due.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const token = localStorage.getItem('streakfree_token');
              const res = await fetch(`${API_URL}/api/squeegee-sync/reset-future-completed`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await res.json();
              if (data.fixed_count === 0) {
                toast.success('No ghost completions found — schedule is clean ✓');
              } else {
                toast.success(`Fixed ${data.fixed_count} ghost completion${data.fixed_count !== 1 ? 's' : ''} (${data.case_a_count} simple + ${data.case_b_count} recurring)`);
              }
            }}
            data-testid="reset-ghosts-btn"
          >
            🧹 Reset future ghost completions
          </Button>
        </div>
      )}

      {step === 'upload' && backups.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-zinc-900 mb-1">Recent applies</h3>
          <p className="text-xs text-zinc-500 mb-3">If anything looks wrong after an apply, you can roll back at any time.</p>
          <div className="space-y-2">
            {backups.slice(0, 10).map(b => {
              const created = new Date(b.created_at);
              const summary = b.summary || {};
              const isRolledBack = b.rolled_back || b.rolled_back_at;
              return (
                <div
                  key={b.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${isRolledBack ? 'bg-zinc-50 border-zinc-200 opacity-60' : 'bg-white border-zinc-200'}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900">
                      {created.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {summary.new_customers ?? 0} new customers · {summary.customers_updated ?? 0} customers updated · {summary.jobs_updated ?? 0} jobs updated
                      {isRolledBack && <span className="ml-2 text-amber-600 font-medium">— rolled back</span>}
                    </p>
                  </div>
                  {!isRolledBack && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRollbackBackup(b.id)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`rollback-backup-${b.id}`}
                    >
                      <RotateCcw size={14} className="mr-1" /> Roll back
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── STEP: PREVIEW ─── */}
      {step === 'preview' && preview && (
        <>
          {/* Summary header */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'New customers', value: preview.summary.new_customers, color: 'bg-emerald-50 text-emerald-700' },
              { label: 'Changed customers', value: preview.summary.changed_customers, color: 'bg-amber-50 text-amber-700' },
              { label: 'Changed jobs', value: preview.summary.changed_jobs, color: 'bg-amber-50 text-amber-700' },
              { label: 'Not on Squeegee', value: preview.summary.sf_only_jobs ?? 0, color: 'bg-red-50 text-red-700' },
              { label: 'Past visits missing', value: preview.summary.done_visits ?? 0, color: 'bg-emerald-50 text-emerald-700' },
            ].map((s, i) => (
              <div key={i} className={`p-3 rounded-xl ${s.color}`}>
                <p className="text-xs uppercase tracking-wider opacity-70">{s.label}</p>
                <p className="text-2xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Sticky apply bar */}
          <div className="sticky top-0 z-10 bg-white border border-zinc-200 rounded-xl p-3 shadow-sm flex items-center gap-3 flex-wrap">
            <p className="text-sm font-medium flex-1 min-w-[200px]">
              {(() => {
                const t = totalSelected();
                return `${t.newC} new · ${t.custFields} customer fields · ${t.jobFields} job fields · ${t.removeJ} to remove · ${t.doneV} visits`;
              })()}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={presetAlignSchedule}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="preset-align-schedule-btn"
            >
              🎯 Align schedule with Squeegee
            </Button>
            <Button variant="outline" size="sm" onClick={() => setStep('upload')} data-testid="back-to-upload-btn">
              ← Start over
            </Button>
            <Button
              onClick={handleApply}
              disabled={totalSelected().total === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="apply-changes-btn"
            >
              Apply {totalSelected().total} changes <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>

          {/* New customers */}
          <Section title="New customers in Squeegee" count={preview.summary.new_customers} color="green" icon={Users} defaultOpen>
            <p className="text-xs text-emerald-700 bg-emerald-50 border-b border-emerald-100 px-3 py-2">
              Each ticked customer is created WITH their Squeegee jobs — same frequency, same next due, same price and round ({preview.summary.new_customer_jobs ?? 0} scheduled jobs in total).
            </p>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Button variant="outline" size="sm" onClick={toggleAllNewCustomers} data-testid="toggle-all-new-customers">
                  {selectedNewCustomers.size === preview.new_customers.length ? 'Deselect all' : 'Select all'}
                </Button>
                <span className="text-xs text-zinc-500">{selectedNewCustomers.size} of {preview.new_customers.length} selected</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-100">
                      <th className="p-2 w-8"></th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Address</th>
                      <th className="p-2">Phone</th>
                      <th className="p-2">Round</th>
                      <th className="p-2">Balance</th>
                      <th className="p-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.new_customers.map((c) => (
                      <tr key={c.external_ref} className="border-b border-zinc-50 hover:bg-zinc-50">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedNewCustomers.has(c.external_ref)}
                            onChange={() => toggleNewCustomer(c.external_ref)}
                          />
                        </td>
                        <td className="p-2 font-medium">{c.name}</td>
                        <td className="p-2 text-zinc-600 text-xs">{c.address}</td>
                        <td className="p-2 text-zinc-600 text-xs">{c.phone}</td>
                        <td className="p-2 text-zinc-600 text-xs">{c.round}</td>
                        <td className="p-2 text-zinc-600 text-xs">£{(c.balance || 0).toFixed(2)}</td>
                        <td className="p-2 text-zinc-600 text-xs max-w-[220px] truncate" title={c.notes || ''}>{c.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* Changed customers */}
          <Section title="Changed customers" count={preview.summary.changed_customers} color="amber" icon={Users}>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={selectAllCustomerFields}>Select all changes</Button>
                <Button variant="outline" size="sm" onClick={clearCustomerFields}>Clear</Button>
                <span className="text-xs text-zinc-500">
                  {Object.values(selectedCustomerFields).reduce((a, s) => a + s.size, 0)} fields selected
                </span>
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-50">
                    <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-100">
                      <th className="p-2">Customer</th>
                      <th className="p-2">Field</th>
                      <th className="p-2">SF</th>
                      <th className="p-2">→ Squeegee</th>
                      <th className="p-2 w-12 text-center">Apply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changed_customers.flatMap(c =>
                      Object.entries(c.diff).map(([field, vals]) => (
                        <tr key={`${c.sf_customer_id}-${field}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                          <td className="p-2 font-medium text-xs">{c.name}</td>
                          <td className="p-2 text-xs text-zinc-700">{field}</td>
                          <td className="p-2 text-xs text-zinc-500"><DiffCell value={vals.sf} /></td>
                          <td className="p-2 text-xs text-emerald-700 font-medium"><DiffCell value={vals.sq} /></td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={(selectedCustomerFields[c.sf_customer_id] || new Set()).has(field)}
                              onChange={() => toggleCustomerField(c.sf_customer_id, field)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* Changed jobs */}
          <Section title="Changed jobs (schedule drift)" count={preview.summary.changed_jobs} color="amber" icon={Briefcase}>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => selectAllJobFields()}>Select all changes</Button>
                <Button variant="outline" size="sm" onClick={() => selectAllJobFields('next_due')}>Only next_due</Button>
                <Button variant="outline" size="sm" onClick={() => selectAllJobFields('last_done')}>Only last_done</Button>
                <Button variant="outline" size="sm" onClick={clearJobFields}>Clear</Button>
                <span className="text-xs text-zinc-500">
                  {Object.values(selectedJobFields).reduce((a, s) => a + s.size, 0)} fields selected
                </span>
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-50">
                    <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-100">
                      <th className="p-2">Customer / Address</th>
                      <th className="p-2">Field</th>
                      <th className="p-2">SF</th>
                      <th className="p-2">→ Squeegee</th>
                      <th className="p-2 w-12 text-center">Apply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changed_jobs.flatMap(j =>
                      Object.entries(j.diff).map(([field, vals]) => (
                        <tr key={`${j.sf_job_id}-${field}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                          <td className="p-2 text-xs">
                            <p className="font-medium">{j.customer_name}</p>
                            <p className="text-zinc-500 truncate max-w-xs">{j.address}</p>
                          </td>
                          <td className="p-2 text-xs text-zinc-700">{field}</td>
                          <td className="p-2 text-xs text-zinc-500"><DiffCell value={vals.sf} /></td>
                          <td className="p-2 text-xs text-emerald-700 font-medium"><DiffCell value={vals.sq} /></td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={(selectedJobFields[j.sf_job_id] || new Set()).has(field)}
                              onChange={() => toggleJobField(j.sf_job_id, field)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          {/* Only in SF */}
          {preview.summary.only_in_sf > 0 && (
            <Section title="Customers only in StreakFree (review only — won't be touched)" count={preview.summary.only_in_sf} color="blue" icon={AlertTriangle}>
              <div className="p-3 max-h-[300px] overflow-y-auto">
                <p className="text-xs text-zinc-500 mb-2">These customers exist in SF but not in your Squeegee export. They'll be left untouched. Review and decide later if any should be archived.</p>
                <ul className="space-y-1 text-sm">
                  {preview.only_in_sf.map(c => (
                    <li key={c.sf_customer_id} className="text-zinc-700">{c.name} <span className="text-zinc-400 text-xs">— {c.address}</span></li>
                  ))}
                </ul>
              </div>
            </Section>
          )}

          {/* New jobs */}
          {preview.summary.new_jobs > 0 && (
            <Section title="New jobs from Squeegee" count={preview.summary.new_jobs} color="green" icon={Briefcase}>
              <div className="p-3 max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-zinc-500">Jobs Squeegee has for customers already in StreakFree — ticked ones are created with their Squeegee schedule.</p>
                  <button
                    onClick={() => setSelectedNewJobs(s => s.size === preview.new_jobs.length ? new Set() : new Set(preview.new_jobs.map((_, i) => i)))}
                    className="text-xs font-semibold text-emerald-700 shrink-0 ml-2"
                    data-testid="newjobs-select-all"
                  >
                    {selectedNewJobs.size === preview.new_jobs.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <ul className="space-y-2 text-sm">
                  {preview.new_jobs.map((j, i) => (
                    <li key={i} className="border-l-2 border-emerald-500 pl-3 flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedNewJobs.has(i)}
                        onChange={() => setSelectedNewJobs(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                        className="w-4 h-4 mt-0.5 accent-emerald-600"
                        data-testid={`newjob-check-${i}`}
                      />
                      <div>
                        <p className="font-medium">{j.customer_name}</p>
                        <p className="text-xs text-zinc-500">{j.service} — {j.frequency} — next due {j.next_due || 'not booked'} — £{(j.price || 0).toFixed(2)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Section>
          )}

          {/* Stale SF jobs not on Squeegee — remove to match the planner */}
          {(preview.summary.sf_only_jobs ?? 0) > 0 && (
            <Section title="Jobs not on Squeegee (stale)" count={preview.summary.sf_only_jobs} color="red" icon={Briefcase}>
              <p className="text-xs text-red-700 bg-red-50 border-b border-red-100 px-3 py-2">
                These active StreakFree jobs don't match anything in your Squeegee export — usually stale duplicates from older imports. Ticked jobs are DELETED on apply (a rollback restores them).
              </p>
              <div className="p-3 max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-zinc-500">{selectedRemoveJobs.size} of {preview.sf_only_jobs.length} selected for removal</span>
                  <button
                    onClick={() => setSelectedRemoveJobs(s => s.size === preview.sf_only_jobs.length ? new Set() : new Set(preview.sf_only_jobs.map(j => j.sf_job_id)))}
                    className="text-xs font-semibold text-red-700 shrink-0 ml-2"
                    data-testid="removejobs-select-all"
                  >
                    {selectedRemoveJobs.size === preview.sf_only_jobs.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <ul className="space-y-2 text-sm">
                  {preview.sf_only_jobs.map((j) => (
                    <li key={j.sf_job_id} className="border-l-2 border-red-400 pl-3 flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedRemoveJobs.has(j.sf_job_id)}
                        onChange={() => setSelectedRemoveJobs(s => { const n = new Set(s); n.has(j.sf_job_id) ? n.delete(j.sf_job_id) : n.add(j.sf_job_id); return n; })}
                        className="w-4 h-4 mt-0.5 accent-red-600"
                        data-testid={`removejob-check-${j.sf_job_id}`}
                      />
                      <div>
                        <p className="font-medium">{j.customer_name}</p>
                        <p className="text-xs text-zinc-500">{j.service} — {j.frequency || 'no frequency'} — due {j.next_due || '?'} — £{(j.price || 0).toFixed(2)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Section>
          )}

          {/* Past completed visits missing from SF */}
          {(preview.summary.done_visits ?? 0) > 0 && (
            <Section title="Past visits done on Squeegee" count={preview.summary.done_visits} color="green" icon={Briefcase}>
              <div className="p-3">
                <label className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDoneVisits}
                    onChange={() => setIncludeDoneVisits(v => !v)}
                    className="w-4 h-4 mt-0.5 accent-emerald-600"
                    data-testid="donevisits-toggle"
                  />
                  <span className="text-emerald-800">
                    Recreate each job's most recent clean (from Squeegee's Last Done) as a completed visit, so past planner days show the work as done. Doesn't touch invoices or revenue reports — purely history. Rollback removes them.
                  </span>
                </label>
                <ul className="mt-3 space-y-1.5 text-sm max-h-[240px] overflow-y-auto">
                  {preview.done_visits.slice(0, 30).map((v, i) => (
                    <li key={i} className="border-l-2 border-emerald-400 pl-3">
                      <span className="font-medium">{v.customer_name}</span>
                      <span className="text-xs text-zinc-500"> — {v.service} done {v.date} — £{(v.price || 0).toFixed(2)}</span>
                    </li>
                  ))}
                  {preview.done_visits.length > 30 && (
                    <li className="text-xs text-zinc-400 pl-3">…and {preview.done_visits.length - 30} more</li>
                  )}
                </ul>
              </div>
            </Section>
          )}
        </>
      )}

      {/* ─── STEP: APPLYING ─── */}
      {step === 'applying' && (
        <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-emerald-600 mb-3" />
          <p className="font-medium text-zinc-900">Applying changes…</p>
          <p className="text-xs text-zinc-500 mt-1">Creating backup and updating records.</p>
        </div>
      )}

      {/* ─── STEP: DONE ─── */}
      {step === 'done' && applyResult && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center space-y-4">
          <CheckCircle size={48} className="mx-auto text-emerald-600" />
          <h2 className="text-xl font-semibold text-zinc-900">Sync complete</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl mx-auto">
            {[
              { label: 'New customers added', value: applyResult.new_customers },
              { label: 'Jobs created', value: applyResult.jobs_created ?? 0 },
              { label: 'Jobs updated', value: applyResult.jobs_updated },
              { label: 'Stale jobs removed', value: applyResult.jobs_removed ?? 0 },
              { label: 'Past visits added', value: applyResult.done_visits_created ?? 0 },
            ].map((s, i) => (
              <div key={i} className="bg-zinc-50 rounded-lg p-3">
                <p className="text-xs uppercase text-zinc-500">{s.label}</p>
                <p className="text-2xl font-bold text-zinc-900">{s.value}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-zinc-500">Backup ID: <code className="bg-zinc-100 px-1 rounded">{applyResult.backup_id}</code></p>

          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={handleRollback} data-testid="rollback-btn">
              <RotateCcw size={14} className="mr-2" /> Roll back
            </Button>
            <Button
              onClick={() => {
                setStep('upload');
                setPreview(null);
                setApplyResult(null);
                setSelectedNewCustomers(new Set());
                setSelectedNewJobs(new Set());
                setSelectedRemoveJobs(new Set());
                setIncludeDoneVisits(false);
                setSelectedCustomerFields({});
                setSelectedJobFields({});
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="done-btn"
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// ───────────────────────────────────────────────
// Schedule Match Tab
// ───────────────────────────────────────────────
const ScheduleMatchTab = () => {
  const [jobsFile, setJobsFile] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedWeek, setExpandedWeek] = useState(null);

  const handleRun = async () => {
    if (!jobsFile) {
      setError('Please pick your Squeegee jobs.csv');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const fd = new FormData();
      fd.append('jobs_csv', jobsFile);
      const res = await fetch(`${API_URL}/api/squeegee-sync/schedule-match?weeks=${weeks}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Comparison failed');
      }
      setReport(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatWeek = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <>
      {/* Upload form */}
      {!report && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <p className="text-sm text-zinc-600">
            Compare your Squeegee schedule against StreakFree week-by-week.
            No data is changed — this is a read-only report.
          </p>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Squeegee Jobs CSV (Reports → All Jobs)</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setJobsFile(e.target.files[0])}
              className="block w-full text-sm border border-zinc-200 rounded-lg p-2"
              data-testid="schedule-match-jobs-input"
            />
            {jobsFile && <p className="text-xs text-emerald-600 mt-1">✓ {jobsFile.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Weeks ahead to compare</label>
            <select
              value={weeks}
              onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
              className="border border-zinc-200 rounded-lg px-3 py-2 text-sm"
              data-testid="schedule-match-weeks-select"
            >
              {[4, 8, 12, 16, 26, 52].map(w => (
                <option key={w} value={w}>{w} weeks</option>
              ))}
            </select>
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          <Button
            onClick={handleRun}
            disabled={!jobsFile || isLoading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="run-schedule-match-btn"
          >
            {isLoading ? <><Loader2 size={16} className="animate-spin mr-2" /> Comparing schedules…</> : <><BarChart3 size={16} className="mr-2" /> Run schedule match</>}
          </Button>
        </div>
      )}

      {/* Report */}
      {report && (
        <>
          {/* Headline match rate */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl p-5">
            <p className="text-xs uppercase tracking-wider opacity-80">Match rate ({report.from_date} → {report.to_date})</p>
            <p className="text-5xl font-bold mt-1">{report.summary.match_rate_pct}%</p>
            <p className="text-sm opacity-90 mt-1">
              {report.summary.match} jobs match exactly out of {report.summary.sq} expected from Squeegee
            </p>
          </div>

          {/* Headline stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { label: 'Drifted within week', value: report.summary.drift_in_week ?? report.summary.drifted, color: 'bg-amber-50 text-amber-700' },
              { label: 'Drifted across weeks', value: report.summary.drift_across_weeks || 0, color: 'bg-orange-50 text-orange-700' },
              { label: 'Only in Squeegee', value: report.summary.only_sq, color: 'bg-blue-50 text-blue-700' },
              { label: 'Only in StreakFree', value: report.summary.only_sf, color: 'bg-purple-50 text-purple-700' },
              { label: 'Weeks compared', value: report.summary.weeks_compared, color: 'bg-zinc-50 text-zinc-700' },
            ].map((s, i) => (
              <div key={i} className={`p-3 rounded-xl ${s.color}`}>
                <p className="text-xs uppercase tracking-wider opacity-70">{s.label}</p>
                <p className="text-2xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Per-week breakdown */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-xs uppercase text-zinc-500">
                  <th className="p-3 w-8"></th>
                  <th className="p-3">Week</th>
                  <th className="p-3 text-right">Sq</th>
                  <th className="p-3 text-right">SF</th>
                  <th className="p-3 text-right">Match</th>
                  <th className="p-3 text-right" title="Same customer, off by ≤6 days">Drift week</th>
                  <th className="p-3 text-right" title="Same customer, off by 7-21 days (likely whole-job shifted)">Drift cross</th>
                  <th className="p-3 text-right">Only Sq</th>
                  <th className="p-3 text-right">Only SF</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.weeks.map((w) => {
                  const isExpanded = expandedWeek === w.week_start;
                  const driftAcross = w.drift_across_weeks || [];
                  const totalIssues = w.drifted.length + driftAcross.length + w.only_in_sq.length + w.only_in_sf.length;
                  return (
                    <React.Fragment key={w.week_start}>
                      <tr
                        className={`border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 ${isExpanded ? 'bg-zinc-50' : ''}`}
                        onClick={() => setExpandedWeek(isExpanded ? null : w.week_start)}
                      >
                        <td className="p-3">
                          {totalIssues > 0 && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                        </td>
                        <td className="p-3 font-medium text-zinc-900">Week of {formatWeek(w.week_start)}</td>
                        <td className="p-3 text-right">{w.sq_count}</td>
                        <td className="p-3 text-right">{w.sf_count}</td>
                        <td className="p-3 text-right text-emerald-600 font-medium">{w.match_count}</td>
                        <td className="p-3 text-right text-amber-600">{w.drifted.length || ''}</td>
                        <td className="p-3 text-right text-orange-600">{driftAcross.length || ''}</td>
                        <td className="p-3 text-right text-blue-600">{w.only_in_sq.length || ''}</td>
                        <td className="p-3 text-right text-purple-600">{w.only_in_sf.length || ''}</td>
                        <td className="p-3">
                          {w.status === 'exact_match' ? (
                            <span className="text-emerald-600 text-xs font-medium">✓ Match</span>
                          ) : w.status === 'drifted_only' ? (
                            <span className="text-amber-600 text-xs font-medium">⚠ Drift only</span>
                          ) : (
                            <span className="text-red-600 text-xs font-medium">✗ Mismatch</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && totalIssues > 0 && (
                        <tr className="bg-zinc-50">
                          <td colSpan={10} className="p-4 space-y-3">
                            {w.drifted.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">⚠ Same customer, off by a few days (within week)</p>
                                <div className="bg-white rounded-lg border border-zinc-200 max-h-60 overflow-y-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-amber-50">
                                      <tr><th className="p-2 text-left">Customer</th><th className="p-2 text-left">SF says</th><th className="p-2 text-left">Squeegee says</th><th className="p-2 text-right">Off by</th></tr>
                                    </thead>
                                    <tbody>
                                      {w.drifted.map((d, i) => (
                                        <tr key={i} className="border-t border-zinc-100">
                                          <td className="p-2 font-medium">{d.name}</td>
                                          <td className="p-2 text-zinc-600">{d.sf_date}</td>
                                          <td className="p-2 text-zinc-600">{d.sq_date}</td>
                                          <td className="p-2 text-right text-amber-600 font-medium">{d.diff_days ?? ''}d</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {driftAcross.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">⚠ Same customer, scheduled in a different week</p>
                                <div className="bg-white rounded-lg border border-zinc-200 max-h-60 overflow-y-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-orange-50">
                                      <tr><th className="p-2 text-left">Customer</th><th className="p-2 text-left">SF says</th><th className="p-2 text-left">Squeegee says</th><th className="p-2 text-right">Off by</th></tr>
                                    </thead>
                                    <tbody>
                                      {driftAcross.map((d, i) => (
                                        <tr key={i} className="border-t border-zinc-100">
                                          <td className="p-2 font-medium">{d.name}</td>
                                          <td className="p-2 text-zinc-600">{d.sf_date}</td>
                                          <td className="p-2 text-zinc-600">{d.sq_date}</td>
                                          <td className="p-2 text-right text-orange-600 font-medium">{d.diff_days ?? ''}d</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {w.only_in_sq.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">→ Squeegee schedules these jobs this week — no SF counterpart in 12-week window</p>
                                <div className="bg-white rounded-lg border border-zinc-200 max-h-60 overflow-y-auto">
                                  <ul className="text-xs">
                                    {w.only_in_sq.slice(0, 50).map((it, i) => (
                                      <li key={i} className="p-2 border-t border-zinc-100"><span className="font-medium">{it.name}</span> <span className="text-zinc-500">— {it.date}</span></li>
                                    ))}
                                    {w.only_in_sq.length > 50 && <li className="p-2 text-zinc-500 text-center">…and {w.only_in_sq.length - 50} more</li>}
                                  </ul>
                                </div>
                              </div>
                            )}
                            {w.only_in_sf.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">← StreakFree schedules these jobs this week — no Squeegee counterpart in 12-week window</p>
                                <div className="bg-white rounded-lg border border-zinc-200 max-h-60 overflow-y-auto">
                                  <ul className="text-xs">
                                    {w.only_in_sf.slice(0, 50).map((it, i) => (
                                      <li key={i} className="p-2 border-t border-zinc-100"><span className="font-medium">{it.name || it.ref}</span> <span className="text-zinc-500">— {it.date}</span></li>
                                    ))}
                                    {w.only_in_sf.length > 50 && <li className="p-2 text-zinc-500 text-center">…and {w.only_in_sf.length - 50} more</li>}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Helper banner */}
          {report.summary.match_rate_pct < 95 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">
                    Match rate is {report.summary.match_rate_pct}% — there's drift to fix
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    Use the "Sync customers & jobs" tab to upload both CSVs and apply the next_due / frequency changes. That should bring the match rate above 95%.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <Button variant="outline" onClick={() => setReport(null)} data-testid="schedule-match-reset-btn">
              ← Run again
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default SqueegeeSync;
